import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InsightsService } from '../../../services/insights-service';
import { UserService } from '../../../services/user-service';
import { UtilService } from '../../../services/util-service';

export interface IInsightsDashboard {
  dashboardId: number;
  name: string;
  description: string;
  photo?: string;
  hasAccess: boolean;
}

/**
 * Insights request-access page: user selects which dashboards to request access for,
 * agrees to the terms, and submits. Uses the same dashboard card styling as the welcome page.
 */
@Component({
  selector: 'app-request-access-page',
  templateUrl: './request-access-page.component.html',
  styleUrls: ['./request-access-page.component.scss'],
  standalone: false,
})
export class RequestAccessPageComponent implements OnInit {
  dashboards: IInsightsDashboard[] = [];
  /** Set of dashboard names currently selected for request. */
  selectedNames: Set<string> = new Set();
  agreed = false;
  isLoading = true;
  submitting = false;
  submitted = false;
  errorMessage: string | null = null;

  constructor(
    public insightsService: InsightsService,
    private userService: UserService,
    private utilService: UtilService,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    if (this.userService.hasInsightsAccess()) {
      this.router.navigate(['/insights']);
      return;
    }

    this.insightsService.getMyDashboards().subscribe({
      next: (data: IInsightsDashboard[]) => {
        this.isLoading = false;
        this.dashboards = data;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  /** Display name for AltiusInsights (brand). */
  get insightsBrand(): string {
    return 'AltiusInsights';
  }

  /** Company name for the copy, e.g. "Demo Corp". */
  get companyName(): string {
    return this.userService.company?.name || 'your organisation';
  }

  /** Dashboards that can be requested (no access yet). */
  get requestableDashboards(): IInsightsDashboard[] {
    return this.dashboards.filter(d => !d.hasAccess);
  }

  isSelected(dashboard: IInsightsDashboard): boolean {
    return this.selectedNames.has(dashboard.name);
  }

  /** Toggles the request checkbox for a dashboard (only for requestable ones). */
  toggleSelection(dashboard: IInsightsDashboard): void {
    if (dashboard.hasAccess) {
      return;
    }
    if (this.selectedNames.has(dashboard.name)) {
      this.selectedNames.delete(dashboard.name);
    } else {
      this.selectedNames.add(dashboard.name);
    }
    this.selectedNames = new Set(this.selectedNames);
  }

  get canSubmit(): boolean {
    return this.agreed && this.selectedNames.size > 0 && !this.submitting;
  }

  /**
   * Handles the REQUEST ACCESS button: validates selection, calls the request-access API with
   * isFirstTimeRequest=true and selected dashboard names. On success, shows confirmation view
   * (like the request-access modal). On error, shows toast and inline error message.
   */
  onSubmit(): void {
    if (!this.agreed) {
      this.errorMessage = 'Please agree to the authorisation statement.';
      return;
    }
    const dashboardNames = Array.from(this.selectedNames);
    if (!dashboardNames.length) {
      this.errorMessage = 'Please select at least one dashboard to request.';
      return;
    }
    this.submitting = true;
    this.errorMessage = null;
    this.insightsService.requestAccess(true, dashboardNames).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = true;
        this.insightsService.showRequestAccessSuccessModal();
      },
      error: () => {
        this.submitting = false;
        this.errorMessage = 'Failed to submit access request. Please try again.';
        this.utilService.showToastError(this.errorMessage);
      },
    });
  }
}
